import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import React, { useCallback, useEffect, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useMilestone } from "../contexts/MilestoneContext";
import type { Property } from "../models/property.model";
import type { ApplicationUser } from "../models/user.model";
import { cachedMasterDataService } from "../service/cachedMasterData.service";
import { categoryService } from "../service/category.service";
import { locationService } from "../service/location.service";
import { propertyService } from "../service/property.service";
import { slugify } from "../utils/slug";
import "./B2BPopup.css";
import AutocompleteInput, { ListItem } from "./Controls/AutocompleteInput";
import Footer from "./Footer";
import LoadingSpinner from "./LoadingSpinner";
import SEO from "./SEO";

const defaultCenter = {
  lat: 11.0168, // Coimbatore latitude
  lng: 76.9558, // Coimbatore longitude
};

// Component to display distance between user and property
const DistanceDisplay: React.FC<{
  propertyAddress: string;
  calculateDistance: (address: string) => Promise<string>;
}> = ({ propertyAddress, calculateDistance }) => {
  const [distance, setDistance] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const getDistance = async () => {
      setLoading(true);
      try {
        const calculatedDistance = await calculateDistance(propertyAddress);
        setDistance(calculatedDistance);
      } catch (error) {
        console.error("Error calculating distance:", error);
        setDistance("");
      } finally {
        setLoading(false);
      }
    };

    if (propertyAddress) {
      getDistance();
    }
  }, [propertyAddress, calculateDistance]);

  if (loading) {
    return (
      <div className="text-center py-1">
        <small className="text-muted">Calculating distance...</small>
      </div>
    );
  }

  if (!distance) {
    return null;
  }

  return (
    <div className="text-center py-1">
      <small className="text-primary fw-bold">
        <Icon.GeoAlt className="me-1" />
        Approx {distance}
      </small>
    </div>
  );
};

const ListProperties: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    cityId = "",
    roomTypeId = 0,
    checkIn = "",
    checkOut = "",
  } = location.state || {};
  const [dateRange, setDateRange] = useState<[Date | null, Date | null] | null>(
    checkIn && checkOut ? [new Date(checkIn), new Date(checkOut)] : null
  );

  // Redux selectors for logged-in user info
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  // Try to get basic auth user object (may contain id/name/email) from redux as a fallback
  const authUser = useSelector((state: any) => state.auth?.user) ?? null;

  // Milestone context
  const { setCurrentStep } = useMilestone();

  // Set milestone to step 1 when B2B users are on properties list
  useEffect(() => {
    if (userRole === "B2BUser") {
      setCurrentStep(1);
    }
  }, [userRole, setCurrentStep]);
  // rmcId removed (was only used for proposals)

  const [localCheckIn, setLocalCheckIn] = useState(checkIn);
  const [localCheckOut, setLocalCheckOut] = useState(checkOut);
  const [properties, setProperties] = useState<any[]>([]);
  const [allProperties, setAllProperties] = useState<Property[]>([]); // Store all loaded properties for filtering
  const [userDetails, setUserDetails] = useState<ApplicationUser | null>(null);
  // Helper: produce a per-user sessionStorage key to remember that the B2B popup
  // has been shown for the currently logged in user. We prefer stable ids, fall
  // back to email or name if id isn't available.
  const getB2BPopupStorageKey = useCallback(() => {
    try {
      const ud: any = userDetails;
      const uid =
        (ud && (ud.id || ud.userId)) ||
        authUser?.id ||
        (authUser as any)?.userId ||
        authUser?.email ||
        authUser?.name;
      if (!uid) return null;
      return `b2bPopupShown:${String(uid)}`;
    } catch (e) {
      return null;
    }
  }, [authUser, userDetails]);

  // B2B user booking data from API response
  const [userCheckIn, setUserCheckIn] = useState<string>("");
  const [userCheckOut, setUserCheckOut] = useState<string>("");
  const [userNoOfAdults, setUserNoOfAdults] = useState<number>(0);
  const [userNoOfChildrens, setUserNoOfChildrens] = useState<number>(0);

  const [coordinates, setCoordinates] = useState<
    { lat: number; lng: number }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number | null>(
    null
  );
  const [roomTypes, setRoomTypes] = useState<ListItem[]>([]);
  const [roomTypeInput, setRoomTypeInput] = useState("");
  const [selectedRoomType, setSelectedRoomType] = useState<ListItem | null>(
    roomTypeId ? { id: roomTypeId, name: "" } : null
  );
  const [cities, setCities] = useState<ListItem[]>([]);
  // cityInput removed (was only used for proposal-specific label behavior)
  const [selectedCity, setSelectedCity] = useState<ListItem | null>(null);

  // Removed proposal selection feature (proposalOptions / selectedProposal)
  // B2B notification popup state
  const [showB2BPopup, setShowB2BPopup] = useState(false);
  const [b2bPopupMessage, setB2bPopupMessage] = useState("");
  const [initialPropertiesLoaded, setInitialPropertiesLoaded] = useState(false);
  const [popupShownThisMount, setPopupShownThisMount] = useState(false);

  // B2BUser advanced filters
  const [locations, setLocations] = useState<ListItem[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<ListItem | null>(
    null
  );
  const [selectedDistance, setSelectedDistance] = useState<number | null>(null);
  const [categories, setCategories] = useState<ListItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<ListItem | null>(
    null
  );
  const [propertyTypes, setPropertyTypes] = useState<ListItem[]>([]);
  const [selectedPropertyType, setSelectedPropertyType] =
    useState<ListItem | null>(null);

  // Track if data has been loaded to prevent duplicate API calls
  const [roomTypesLoaded, setRoomTypesLoaded] = useState(false);
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [propertyTypesLoaded, setPropertyTypesLoaded] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPropertyTypes, setLoadingPropertyTypes] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBFHkJCSUyW7FqPZAHPD0NpNdPWMmDcIa8",
    libraries: ["places"],
  });

  const geocodeAddresses = useCallback((addresses: string[]) => {
    // Wait for Google Maps API to be loaded and use the correct constructor
    if (
      !(
        window.google &&
        window.google.maps &&
        typeof window.google.maps.Geocoder === "function"
      )
    ) {
      console.error(
        "Google Maps API is not loaded or Geocoder is not available."
      );
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    const promises = addresses.map((address) => {
      return new Promise<{ lat: number; lng: number }>((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const { lat, lng } = results[0].geometry.location;
            resolve({ lat: lat(), lng: lng() });
          } else {
            console.warn("Geocode failed:", address, status);
            resolve(defaultCenter);
          }
        });
      });
    });
    Promise.all(promises).then(setCoordinates);
  }, []);

  // Helper: normalize and return image URLs for a property object
  const getPropertyImageUrls = useCallback((property: any): string[] => {
    if (!property) return [];
    const raw = property as any;
    const sourceImages =
      raw.ImageDtos?.$values ||
      raw.imageDtos?.$values ||
      raw.ImageDtos ||
      raw.imageDtos ||
      raw.propertyImages?.$values ||
      raw.propertyImages ||
      raw.propertyImages?.$values ||
      null;

    const imgs: string[] = [];

    const toUrl = (rawUrl: any) => {
      if (!rawUrl) return undefined;
      const s = String(rawUrl).trim();
      if (!s) return undefined;
      if (/^https?:\/\//i.test(s) || s.startsWith("/")) return s;
      if (
        /^[\w\-]+\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(s) ||
        s.indexOf("/") === -1
      )
        return `/Images/${s}`;
      return s;
    };

    if (Array.isArray(sourceImages) && sourceImages.length) {
      if (typeof sourceImages[0] === "string") {
        sourceImages.forEach((s: string) => {
          const u = toUrl(s);
          if (u) imgs.push(u);
        });
      } else {
        const getUrl = (pi: any) =>
          toUrl(
            pi &&
              (pi.imageUrl ||
                pi.ImageUrl ||
                pi.url ||
                pi.Url ||
                pi.src ||
                pi.path ||
                pi.fileName ||
                pi.FileName ||
                pi.filePath ||
                pi.FilePath)
          );
        const main = sourceImages.find(
          (pi: any) => pi && (pi.isMainImage || pi.isMain || pi.IsMainImage)
        );
        if (main) {
          const m = getUrl(main);
          if (m) imgs.push(m);
        }
        sourceImages.forEach((pi: any) => {
          const u = getUrl(pi);
          if (!u) return;
          if (main && u === getUrl(main)) return;
          imgs.push(u);
        });
      }
    } else if (
      Array.isArray(raw.propertyImages) &&
      raw.propertyImages.length &&
      typeof raw.propertyImages[0] === "string"
    ) {
      raw.propertyImages.forEach((s: string) => {
        const u = toUrl(s);
        if (u) imgs.push(u);
      });
    } else if (Array.isArray(raw.images) && raw.images.length) {
      raw.images.forEach((s: string) => {
        const u = toUrl(s);
        if (u) imgs.push(u);
      });
    } else if (raw.imageUrl) {
      const u = toUrl(raw.imageUrl);
      if (u) imgs.push(u);
    }

    // Dedupe while preserving order
    const uniq = Array.from(new Set(imgs)).filter(Boolean) as string[];
    return uniq;
  }, []);

  const hasShortlistedRooms = useCallback((property: any): boolean => {
    if (!property) return false;
    return Boolean(property.isShortlistedProperty);
  }, []);

  // Helper: extract unique category names available for a property (from rooms or other fields)
  const getPropertyCategoryNames = useCallback((property: any): string[] => {
    if (!property) return [];
    const names: string[] = [];

    // rooms may be an array with category objects or categoryName fields
    const rooms = property.rooms || property.Rooms || property.roomDtos || null;
    if (Array.isArray(rooms)) {
      rooms.forEach((r: any) => {
        const n =
          (r && r.category && (r.category.name || r.categoryName)) ||
          r?.categoryName ||
          r?.category?.name ||
          null;
        if (n) names.push(String(n).trim());
      });
    }

    // fallback: property may directly contain a categories list or a categoryName
    if (Array.isArray(property.categories)) {
      property.categories.forEach((c: any) => {
        const n =
          c && (c.name || c.categoryName) ? c.name || c.categoryName : null;
        if (n) names.push(String(n).trim());
      });
    }

    if (property.categoryName) {
      names.push(String(property.categoryName).trim());
    }

    if (property.category && property.category.name) {
      names.push(String(property.category.name).trim());
    }

    // Deduplicate preserving order and remove empties
    return Array.from(new Set(names.filter(Boolean)));
  }, []);

  const extractDropdownOptionsFromProperties = useCallback(
    (propertiesData: Property[]) => {
      if (!propertiesData || propertiesData.length === 0) return;

      const uniqueCities = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        if (property.cityId) {
          const cityName =
            property.city?.name ||
            (property as any).City?.name ||
            (property as any).cityName ||
            `City ${property.cityId}`;
          uniqueCities.set(property.cityId, cityName);
        }
      });
      const citiesFromProperties = Array.from(uniqueCities.entries()).map(
        ([id, name]) => ({ id, name })
      );
      if (citiesFromProperties.length > 0) {
        setCities(citiesFromProperties);
        setCitiesLoaded(true);
      }

      const uniqueLocations = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        if (property.locationId && property.location?.name) {
          uniqueLocations.set(property.locationId, property.location.name);
        }
      });
      const locationsFromProperties = Array.from(uniqueLocations.entries()).map(
        ([id, name]) => ({ id, name })
      );
      if (locationsFromProperties.length > 0) {
        setLocations(locationsFromProperties);
        setLocationsLoaded(true);
      }

      const uniquePropertyTypes = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        if (property.propertyTypeId && property.propertyType?.name) {
          uniquePropertyTypes.set(
            property.propertyTypeId,
            property.propertyType.name
          );
        }
      });
      const propertyTypesFromProperties = Array.from(
        uniquePropertyTypes.entries()
      ).map(([id, name]) => ({ id, name }));
      if (propertyTypesFromProperties.length > 0) {
        setPropertyTypes(propertyTypesFromProperties);
        setPropertyTypesLoaded(true);
      }

      const uniqueRoomTypes = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        if (property.roomTypes && Array.isArray(property.roomTypes)) {
          property.roomTypes.forEach((roomType: any) => {
            if (roomType.id && roomType.name) {
              uniqueRoomTypes.set(roomType.id, roomType.name);
            }
          });
        }
      });
      const roomTypesFromProperties = Array.from(uniqueRoomTypes.entries()).map(
        ([id, name]) => ({ id, name })
      );
      if (roomTypesFromProperties.length > 0) {
        setRoomTypes(roomTypesFromProperties);
        setRoomTypesLoaded(true);
      }

      const uniqueCategories = new Map<number | string, string>();
      propertiesData.forEach((property) => {
        const propertyWithRooms = property as any;
        if (propertyWithRooms.rooms && Array.isArray(propertyWithRooms.rooms)) {
          propertyWithRooms.rooms.forEach((room: any) => {
            if (room.category && room.category.id && room.category.name) {
              uniqueCategories.set(room.category.id, room.category.name);
            }
          });
        }
      });
      const categoriesFromProperties = Array.from(
        uniqueCategories.entries()
      ).map(([id, name]) => ({ id, name }));
      if (categoriesFromProperties.length > 0) {
        setCategories(categoriesFromProperties);
        setCategoriesLoaded(true);
      }
    },
    []
  );

  // Utility function to calculate distance between two coordinates using Haversine formula
  const calculateDistance = useCallback(
    (lat1: number, lon1: number, lat2: number, lon2: number): number => {
      const R = 6371; // Radius of the Earth in kilometers
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLon = ((lon2 - lon1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c; // Distance in kilometers
      return distance;
    },
    []
  );

  // Function to get coordinates from address string
  const getCoordinatesFromAddress = useCallback(
    (address: string): Promise<{ lat: number; lng: number }> => {
      return new Promise((resolve) => {
        if (
          !(
            window.google &&
            window.google.maps &&
            typeof window.google.maps.Geocoder === "function"
          )
        ) {
          console.error("Google Maps API is not loaded");
          resolve(defaultCenter);
          return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const { lat, lng } = results[0].geometry.location;
            resolve({ lat: lat(), lng: lng() });
          } else {
            console.warn("Geocode failed for address:", address, status);
            resolve(defaultCenter);
          }
        });
      });
    },
    []
  );

  // Function to calculate distance between user address and property address
  const calculateDistanceToProperty = useCallback(
    async (propertyAddress: string): Promise<string> => {
      if (!userDetails?.address || userDetails.address.length === 0) {
        return "";
      }

      // Get user's primary address (first address in the array)
      const userAddress = userDetails.address[0];
      const userAddressString = [
        userAddress.doorNumber,
        userAddress.buildingName,
        userAddress.address1,
        userAddress.address2,
      ]
        .filter(Boolean)
        .join(", ");

      if (!userAddressString || !propertyAddress) {
        return "";
      }

      try {
        const [userCoords, propertyCoords] = await Promise.all([
          getCoordinatesFromAddress(userAddressString),
          getCoordinatesFromAddress(propertyAddress),
        ]);

        const distance = calculateDistance(
          userCoords.lat,
          userCoords.lng,
          propertyCoords.lat,
          propertyCoords.lng
        );

        return distance < 1
          ? `${Math.round(distance * 1000)} m away`
          : `${distance.toFixed(1)} Km away`;
      } catch (error) {
        console.error("Error calculating distance:", error);
        return "";
      }
    },
    [userDetails, calculateDistance, getCoordinatesFromAddress]
  );

  useEffect(() => {
    setLoading(true);
    // Wait for userRole to be available before deciding which initial load path to take.
    // If userRole is not yet known (e.g. during auth rehydration on refresh), skip
    // the fallback branch so we don't accidentally mark initialPropertiesLoaded
    // for the wrong role and show the incorrect popup message.
    if (
      userRole === "" ||
      userRole === null ||
      typeof userRole === "undefined"
    ) {
      return;
    }

    // For B2BUser, load all eligible properties without requiring cityId
    if (userRole === "B2BUser") {
      propertyService
        .searchPropertiesForB2BUser("", "", "")
        .then((data) => {
          console.log("Initial B2BUser load response:", data);
          if (data.properties) {
            setAllProperties(data.properties);
            setProperties(data.properties);
            extractDropdownOptionsFromProperties(data.properties);
          }
          // Store user details if available
          if (data.user) {
            setUserDetails(data.user);
            console.log("Initial user details stored:", data.user);
            if (data.user.address && data.user.address.length > 0) {
              const userAddress = data.user.address[0]; // Get first address
              console.log("User address object:", userAddress);
              if (userAddress.checkIn) {
                console.log("Setting userCheckIn to:", userAddress.checkIn);
                setUserCheckIn(userAddress.checkIn);
              }
              if (userAddress.checkOut) {
                console.log("Setting userCheckOut to:", userAddress.checkOut);
                setUserCheckOut(userAddress.checkOut);
              }

              if (
                userAddress.noOfAdults !== undefined &&
                userAddress.noOfAdults !== null
              ) {
                console.log(
                  "Setting userNoOfAdults to:",
                  userAddress.noOfAdults
                );
                const adultsVal = Number(userAddress.noOfAdults);
                setUserNoOfAdults(adultsVal);
                try {
                  if (typeof window !== "undefined")
                    sessionStorage.setItem("userNoOfAdults", String(adultsVal));
                } catch {}
              }
              if (
                userAddress.noOfChildrens !== undefined &&
                userAddress.noOfChildrens !== null
              ) {
                console.log(
                  "Setting userNoOfChildrens to:",
                  userAddress.noOfChildrens
                );
                const childVal = Number(userAddress.noOfChildrens);
                setUserNoOfChildrens(childVal);
                try {
                  if (typeof window !== "undefined")
                    sessionStorage.setItem(
                      "userNoOfChildrens",
                      String(childVal)
                    );
                } catch {}
              }
            }
          }

          const validAddresses = data.properties
            .map((p: any) => p.address)
            .filter(Boolean) as string[];
          if (window.google && window.google.maps) {
            geocodeAddresses(validAddresses);
          }
        })
        .finally(() => {
          setLoading(false);
          setInitialPropertiesLoaded(true);
        });
    } else if (cityId && !isNaN(Number(cityId))) {
      // For non-B2BUser, require cityId and use original search
      propertyService
        .searchProperties(cityId, checkIn, checkOut, roomTypeId)
        .then((data) => {
          console.log("Initial load response:", data);

          // Set properties
          if (data.properties) {
            setAllProperties(data.properties);
            setProperties(data.properties);
            extractDropdownOptionsFromProperties(data.properties);
          }

          const validAddresses = data.properties
            .map((p: any) => p.address)
            .filter(Boolean) as string[];
          if (window.google && window.google.maps) {
            geocodeAddresses(validAddresses);
          }
        })
        .finally(() => {
          setLoading(false);
          setInitialPropertiesLoaded(true);
        });
    } else {
      // Non-B2B user and no cityId: deliberately set empty properties but only when
      // userRole is already known (we returned above when role was undefined).
      setProperties([]);
      setAllProperties([]);
      setLoading(false);
      setInitialPropertiesLoaded(true);
    }
  }, [
    cityId,
    geocodeAddresses,
    userRole,
    checkIn,
    checkOut,
    roomTypeId,
    extractDropdownOptionsFromProperties,
  ]);

  // Show popup for B2B users once after initial properties load completes
  useEffect(() => {
    // Debug logging to trace when the popup decision runs
    console.debug("B2B popup effect run:", {
      userRole,
      initialPropertiesLoaded,
      popupShownThisMount,
      propertiesLength: properties ? properties.length : 0,
    });

    if (userRole !== "B2BUser") return;
    if (!initialPropertiesLoaded) return;
    if (popupShownThisMount) return;

    // If a storage key exists for this user and indicates we've shown the popup,
    // don't show it again (persists across refreshes within the same login session).
    const storageKey = getB2BPopupStorageKey();
    if (storageKey) {
      try {
        const shown = sessionStorage.getItem(storageKey);
        if (shown === "1") {
          setPopupShownThisMount(true);
          return;
        }
      } catch (e) {
        // ignore storage errors and continue
      }
    }

    if (properties && properties.length > 0) {
      setB2bPopupMessage(
        "Welcome! We’re delighted to share that a listing has been prepared especially for you."
      );
    } else {
      setB2bPopupMessage(
        "Please be informed that there is currently no listing prepared. We will notify the managing team, and they will reach out to you."
      );
    }
    setShowB2BPopup(true);
    setPopupShownThisMount(true);

    // Persist shown flag for this user so refresh won't show it again
    if (storageKey) {
      try {
        sessionStorage.setItem(storageKey, "1");
      } catch (e) {}
    }
  }, [
    userRole,
    initialPropertiesLoaded,
    properties,
    popupShownThisMount,
    getB2BPopupStorageKey,
  ]);

  // When userRole becomes empty (user logged out), remove stored per-user flag so
  // the popup will show again for the next login.
  useEffect(() => {
    if (!userRole) {
      const key = getB2BPopupStorageKey();
      if (key) {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {}
      }
      setPopupShownThisMount(false);
    }
  }, [userRole, getB2BPopupStorageKey]);

  useEffect(() => {
    if (!roomTypesLoaded && !loadingRoomTypes && roomTypes.length === 0) {
      setLoadingRoomTypes(true);
      cachedMasterDataService
        .getRoomTypesList()
        .then((data) => {
          setRoomTypes(data);
          setRoomTypesLoaded(true);
          setLoadingRoomTypes(false);
        })
        .catch(() => {
          setLoadingRoomTypes(false);
        });
    }
  }, [roomTypesLoaded, loadingRoomTypes, roomTypes.length]);

  useEffect(() => {
    if (!citiesLoaded && !loadingCities && cities.length === 0) {
      setLoadingCities(true);
      cachedMasterDataService
        .getCitiesList()
        .then((cityList) => {
          setCities(cityList);
          setCitiesLoaded(true);
          setLoadingCities(false);
          // Set selectedCity from cityId if present
          if (cityId) {
            const found = cityList.find((c) => String(c.id) === String(cityId));
            setSelectedCity(found || null);
          }
        })
        .catch(() => {
          setLoadingCities(false);
        });
    } else if (cityId && cities.length > 0 && !selectedCity) {
      // Handle case where cityId is set but cities are already loaded
      const found = cities.find((c) => String(c.id) === String(cityId));
      setSelectedCity(found || null);
    }
  }, [citiesLoaded, loadingCities, cityId, cities, selectedCity]);

  useEffect(() => {
    if (!locationsLoaded && !loadingLocations && locations.length === 0) {
      setLoadingLocations(true);
      locationService
        .getListItems()
        .then((data: ListItem[]) => {
          setLocations(data);
          setLocationsLoaded(true);
          setLoadingLocations(false);
        })
        .catch(() => {
          setLoadingLocations(false);
        });
    }

    if (!categoriesLoaded && !loadingCategories && categories.length === 0) {
      setLoadingCategories(true);
      categoryService
        .getListItems()
        .then((data: ListItem[]) => {
          setCategories(data);
          setCategoriesLoaded(true);
          setLoadingCategories(false);
        })
        .catch(() => {
          setLoadingCategories(false);
        });
    }

    if (
      !propertyTypesLoaded &&
      !loadingPropertyTypes &&
      propertyTypes.length === 0
    ) {
      setLoadingPropertyTypes(true);
      cachedMasterDataService
        .getPropertyTypesList()
        .then((data: ListItem[]) => {
          setPropertyTypes(data);
          setPropertyTypesLoaded(true);
          setLoadingPropertyTypes(false);
        })
        .catch(() => {
          setLoadingPropertyTypes(false);
        });
    }
  }, [
    locationsLoaded,
    loadingLocations,
    categoriesLoaded,
    loadingCategories,
    propertyTypesLoaded,
    loadingPropertyTypes,
    locations.length,
    categories.length,
    propertyTypes.length,
  ]);

  // Removed proposal fetching effect

  const handleSearch = () => {
    // Implement client-side filtering instead of API call
    console.log("Applying client-side filters...");

    let filteredProperties = [...allProperties];

    // Filter by city if selected
    if (selectedCity) {
      filteredProperties = filteredProperties.filter(
        (p) => p.cityId === selectedCity.id
      );
    }

    // Filter by room type if selected
    if (selectedRoomType) {
      const searchRoomTypeId = selectedRoomType.id;
      filteredProperties = filteredProperties.filter((p) =>
        p.roomTypes?.some((rt: any) => rt.id === searchRoomTypeId)
      );
    }

    // Filter by location if selected (B2BUser)
    if (selectedLocation) {
      filteredProperties = filteredProperties.filter(
        (p) => p.locationId === selectedLocation.id
      );
    }

    // Filter by category if selected (B2BUser)
    // Note: Category filtering may need to be implemented based on your specific data structure
    // if (selectedCategory) {
    //   filteredProperties = filteredProperties.filter(p =>
    //     // Add category filtering logic here based on your data model
    //   );
    // }
    // Filter by category if selected (B2BUser)
    // The API returns room-level category information as rooms[].category.name
    if (selectedCategory && selectedCategory.id != null) {
      const categoryName = String(selectedCategory.name || "").toLowerCase();
      filteredProperties = filteredProperties.filter((p: any) => {
        const rooms = p.rooms || [];
        if (!Array.isArray(rooms)) return false;
        return rooms.some((r: any) => {
          const rn =
            (r && r.category && r.category.name) || r?.categoryName || "";
          return String(rn).toLowerCase() === categoryName;
        });
      });
    }

    // Filter by property type if selected (B2BUser)
    if (selectedPropertyType) {
      filteredProperties = filteredProperties.filter(
        (p) => p.propertyTypeId === selectedPropertyType.id
      );
    }

    // Filter by distance if selected (B2BUser)
    if (
      userRole === "B2BUser" &&
      selectedDistance &&
      userDetails?.address &&
      userDetails.address.length > 0
    ) {
      const userAddress = userDetails.address[0];
      const userAddressString = [
        userAddress.doorNumber,
        userAddress.buildingName,
        userAddress.address1,
        userAddress.address2,
      ]
        .filter(Boolean)
        .join(", ");

      getCoordinatesFromAddress(userAddressString).then((userCoords) => {
        Promise.all(
          filteredProperties.map(async (property) => {
            if (!property.address) return null;
            try {
              const propertyCoords = await getCoordinatesFromAddress(
                property.address
              );
              const distance = calculateDistance(
                userCoords.lat,
                userCoords.lng,
                propertyCoords.lat,
                propertyCoords.lng
              );
              // Only include properties within selectedDistance (e.g., 5km)
              return distance <= selectedDistance ? property : null;
            } catch {
              return null;
            }
          })
        ).then((results) => {
          const filteredByDistance = results.filter(Boolean);
          setProperties(filteredByDistance);
          const validAddresses = filteredByDistance
            .map((p: any) => p.address)
            .filter(Boolean) as string[];
          if (window.google && window.google.maps) {
            geocodeAddresses(validAddresses);
          }
          console.log(
            `Filtered ${filteredByDistance.length} properties by distance (${selectedDistance}km)`
          );
        });
      });
      return;
    }

    // Update displayed properties
    setProperties(filteredProperties);

    // Update coordinates for filtered properties
    const validAddresses = filteredProperties
      .map((p: any) => p.address)
      .filter(Boolean) as string[];
    if (window.google && window.google.maps) {
      geocodeAddresses(validAddresses);
    }

    console.log(
      `Filtered ${filteredProperties.length} properties from ${allProperties.length} total`
    );
  };

  const handleClearFilters = () => {
    // Reset all filter selections
    setSelectedCity(null);
    setSelectedRoomType(null);
    setSelectedLocation(null);
    setSelectedCategory(null);
    setSelectedPropertyType(null);
    setSelectedDistance(null);

    // Reset properties to show all
    setProperties([...allProperties]);

    // Update coordinates for all properties
    const validAddresses = allProperties
      .map((p: any) => p.address)
      .filter(Boolean) as string[];
    if (window.google && window.google.maps) {
      geocodeAddresses(validAddresses);
    }

    console.log("Filters cleared, showing all properties");
  };

  const handleCityChange = (item: ListItem | null) => {
    setSelectedCity(item);
    // Do not trigger search here
  };

  const listJsonLd = properties.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: properties.map((p, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          url: `${
            typeof window !== "undefined" ? window.location.origin : ""
          }/property/${slugify(p.name)}-${p.id}`,
          name: p.name,
        })),
      }
    : undefined;
  return (
    <>
      {/* B2B notification popup */}
      {showB2BPopup && (
        <div className="b2b-popup-root" role="dialog" aria-modal="true">
          <div className="b2b-popup-backdrop" />
          <div className="b2b-popup-wrap">
            <div className="b2b-popup-card" aria-live="polite">
              <div className="b2b-popup-left">
                <div className="b2b-popup-icon-wrap" aria-hidden>
                  {/* simple document+clock SVG similar to sample */}
                  <svg
                    className="b2b-popup-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="14"
                      height="18"
                      rx="2"
                      stroke="#ff8a00"
                      strokeWidth="1.5"
                      fill="#fff"
                    />
                    <path
                      d="M8 7h6"
                      stroke="#ff8a00"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="18.5"
                      cy="17.5"
                      r="3"
                      stroke="#ff8a00"
                      strokeWidth="1.2"
                    />
                    <path
                      d="M18.5 16.2v1.4l1 0.6"
                      stroke="#ff8a00"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
              <div className="b2b-popup-content">
                {/* Show the logged-in user's name if available */}
                <h4>
                  Hi
                  {userDetails?.firstName ||
                  authUser?.fullName ||
                  authUser?.name
                    ? `, ${
                        userDetails?.firstName
                          ? userDetails.firstName +
                            (userDetails.lastName
                              ? " " + userDetails.lastName
                              : "")
                          : authUser?.fullName || authUser?.name
                      }`
                    : ""}
                </h4>
                <div className="b2b-popup-body">{b2bPopupMessage}</div>
              </div>
              <div className="b2b-popup-actions">
                <button
                  className="b2b-action-primary"
                  onClick={() => setShowB2BPopup(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <SEO
        title={
          selectedCity
            ? `${selectedCity.name} Properties`
            : "Available Properties"
        }
        description={
          "Browse corporate housing properties" +
          (selectedCity ? ` in ${selectedCity.name}` : "")
        }
        canonical={`${
          typeof window !== "undefined" ? window.location.origin : ""
        }/b2b-property`}
        jsonLd={listJsonLd}
      />
      {/* <BookingMilestone currentStep={1} /> */}
      <div className="content">
        <div className="content-header py-3 bg-white border-bottom">
          <div className="container-fluid">
            <div className="row align-items-center">
              <div className="col-12">
                <div className="d-flex flex-wrap gap-3 align-items-center justify-content-center">
                  {/* Filters Section */}
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {/* Distance Filter */}
                    <div className="d-flex align-items-center gap-1">
                      <Dropdown
                        value={selectedDistance}
                        options={[
                          { name: "Any Distance", id: null },
                          { name: "Within 5 Km", id: 5 },
                          { name: "Within 10 Km", id: 10 },
                          { name: "Within 20 Km", id: 20 },
                          { name: "Within 50 Km", id: 50 },
                        ]}
                        onChange={(e) => setSelectedDistance(e.value)}
                        placeholder="Any Distance"
                        optionLabel="name"
                        optionValue="id"
                        className="w-auto"
                        style={{
                          minWidth: "150px",
                          width: "150px",
                          maxWidth: "190px",
                        }}
                        valueTemplate={(option) => (
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="pi pi-map-marker text-muted"
                              style={{ fontSize: "14px", flexShrink: 0 }}
                            ></i>
                            <span>{option ? option.name : "Any Distance"}</span>
                          </div>
                        )}
                        itemTemplate={(option) => (
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="pi pi-map-marker text-muted"
                              style={{ fontSize: "14px" }}
                            ></i>
                            <span title={option.name}>{option.name}</span>
                          </div>
                        )}
                        appendTo="self"
                      />
                    </div>

                    {/* Categories Filter */}
                    {/* <div className="d-flex align-items-center gap-1">
                    <Dropdown
                      value={selectedCategory ? selectedCategory.id : null}
                      options={[{ name: 'All Categories', id: null }, ...categories]}
                      onChange={e => {
                        const category = categories.find((c: any) => c.id === e.value);
                        setSelectedCategory(category || null);
                      }}
                      placeholder="All Categories"
                      optionLabel="name"
                      optionValue="id"
                      className="w-auto"
                      style={{ minWidth: '160px', width: '160px', maxWidth: '200px' }}
                      valueTemplate={(option) => (
                        <div className="d-flex align-items-center gap-2">
                          <i className="pi pi-tags text-muted" style={{ fontSize: '14px', flexShrink: 0 }}></i>
                          <span>{option ? option.name : 'All Categories'}</span>
                        </div>
                      )}
                      itemTemplate={(option) => (
                        <div className="d-flex align-items-center gap-2">
                          <i className="pi pi-tags text-muted" style={{ fontSize: '14px' }}></i>
                          <span title={option.name}>{option.name}</span>
                        </div>
                      )}
                    />
                  </div> */}

                    {/* Property Types Filter */}
                    <div className="d-flex align-items-center gap-1">
                      <Dropdown
                        value={
                          selectedPropertyType ? selectedPropertyType.id : null
                        }
                        options={[
                          { name: "All Property Types", id: null },
                          ...propertyTypes,
                        ]}
                        onChange={(e) => {
                          const propertyType = propertyTypes.find(
                            (pt: any) => pt.id === e.value
                          );
                          setSelectedPropertyType(propertyType || null);
                        }}
                        placeholder="All Property Types"
                        optionLabel="name"
                        optionValue="id"
                        className="w-auto"
                        style={{
                          minWidth: "180px",
                          width: "180px",
                          maxWidth: "220px",
                        }}
                        valueTemplate={(option) => (
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="pi pi-home text-muted"
                              style={{ fontSize: "14px", flexShrink: 0 }}
                            ></i>
                            <span>
                              {option ? option.name : "All Property Types"}
                            </span>
                          </div>
                        )}
                        itemTemplate={(option) => (
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="pi pi-home text-muted"
                              style={{ fontSize: "14px" }}
                            ></i>
                            <span title={option.name}>{option.name}</span>
                          </div>
                        )}
                        appendTo="self"
                      />
                    </div>

                    {/* Room Types Filter */}
                    <div className="d-flex align-items-center gap-1">
                      <Dropdown
                        value={selectedRoomType ? selectedRoomType.id : null}
                        options={[
                          { name: "All Room Types", id: null },
                          ...roomTypes,
                        ]}
                        onChange={(e) => {
                          const roomType = roomTypes.find(
                            (rt: any) => rt.id === e.value
                          );
                          setSelectedRoomType(roomType || null);
                        }}
                        placeholder="All Room Types"
                        optionLabel="name"
                        optionValue="id"
                        className="w-auto"
                        style={{
                          minWidth: "160px",
                          width: "160px",
                          maxWidth: "200px",
                        }}
                        valueTemplate={(option) => (
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="pi pi-th-large text-muted"
                              style={{ fontSize: "14px", flexShrink: 0 }}
                            ></i>
                            <span>
                              {option ? option.name : "All Room Types"}
                            </span>
                          </div>
                        )}
                        itemTemplate={(option) => (
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="pi pi-th-large text-muted"
                              style={{ fontSize: "14px" }}
                            ></i>
                            <span title={option.name}>{option.name}</span>
                          </div>
                        )}
                        appendTo="self"
                      />
                    </div>

                    {/* Cities Filter
                  <div className="d-flex align-items-center gap-1">
                    <Dropdown
                      value={selectedCity ? selectedCity.id : null}
                      options={[{ name: 'All Cities', id: null }, ...cities]}
                      onChange={e => {
                        const city = cities.find((c: any) => c.id === e.value);
                        setSelectedCity(city || null);
                      }}
                      placeholder="All Cities"
                      optionLabel="name"
                      optionValue="id"
                      className="w-auto"
                      style={{ minWidth: '140px', width: '140px', maxWidth: '160px' }}
                      valueTemplate={(option) => (
                        <div className="d-flex align-items-center gap-2">
                          <i className="pi pi-building text-muted" style={{ fontSize: '14px', flexShrink: 0 }}></i>
                          <span>{option ? option.name : 'All Cities'}</span>
                        </div>
                      )}
                      itemTemplate={(option) => (
                        <div className="d-flex align-items-center gap-2">
                          <i className="pi pi-building text-muted" style={{ fontSize: '14px' }}></i>
                          <span title={option.name}>{option.name}</span>
                        </div>
                      )}
                    />
                  </div> */}

                    {/* Locations Filter */}
                    <div className="d-flex align-items-center gap-1">
                      <Dropdown
                        value={selectedLocation ? selectedLocation.id : null}
                        options={[
                          { name: "All Locations", id: null },
                          ...locations,
                        ]}
                        onChange={(e) => {
                          const location = locations.find(
                            (l: any) => l.id === e.value
                          );
                          setSelectedLocation(location || null);
                        }}
                        placeholder="All Locations"
                        optionLabel="name"
                        optionValue="id"
                        className="w-auto"
                        style={{
                          minWidth: "160px",
                          width: "160px",
                          maxWidth: "200px",
                        }}
                        valueTemplate={(option) => (
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="pi pi-globe text-muted"
                              style={{ fontSize: "14px", flexShrink: 0 }}
                            ></i>
                            <span>
                              {option ? option.name : "All Locations"}
                            </span>
                          </div>
                        )}
                        itemTemplate={(option) => (
                          <div className="d-flex align-items-center gap-2">
                            <i
                              className="pi pi-globe text-muted"
                              style={{ fontSize: "14px" }}
                            ></i>
                            <span title={option.name}>{option.name}</span>
                          </div>
                        )}
                        appendTo="self"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex gap-2">
                    <Button
                      onClick={handleSearch}
                      label="Filter"
                      className="p-button-primary"
                      style={{
                        backgroundColor: "#007bff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 16px",
                      }}
                    />
                    <Button
                      onClick={handleClearFilters}
                      label="Clear"
                      className="p-button-outlined"
                      style={{
                        color: "#6c757d",
                        borderColor: "#6c757d",
                        borderRadius: "6px",
                        padding: "8px 16px",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="content-body p-4">
          <div className="container-fluid py-4">
            <div className="row g-4">
              <div className="col-12 col-lg-8">
                {loading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "60vh" }}
                  >
                    <LoadingSpinner size="lg" text="Loading properties..." />
                  </div>
                ) : properties.length === 0 ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "60vh" }}
                  >
                    <div className="text-center">
                      <i
                        className="pi pi-search text-muted"
                        style={{ fontSize: "3rem" }}
                      ></i>
                      <h4 className="mt-4 text-dark">No properties found</h4>
                      <p className="text-muted lead">
                        Try adjusting your filters to see more results.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div
                    className="row row-cols-1 row-cols-sm-2 row-cols-md-4 row-cols-lg-4 g-2"
                    style={{
                      maxHeight: "calc(100vh - 200px)",
                      overflowY: "auto",
                    }}
                  >
                    {properties.map((property) => (
                      <div
                        className="col d-flex align-items-stretch"
                        key={property.id}
                      >
                        <article
                          className="card border-0 shadow-sm h-100 d-flex flex-column"
                          style={{
                            borderRadius: "8px",
                            transition: "all 0.3s ease",
                            cursor: "pointer",
                            maxWidth: "280px",
                            fontSize: "0.85rem",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 25px rgba(0,0,0,0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "";
                          }}
                        >
                          <div
                            className="position-relative overflow-hidden"
                            style={{
                              borderRadius: "8px 8px 0 0",
                              height: "180px",
                            }}
                          >
                            <div className="swiper z-2">
                              {(() => {
                                const urls = getPropertyImageUrls(property);
                                const final = urls.length
                                  ? urls
                                  : ["/Images/no-image-placeholder.png"];
                                return (
                                  <>
                                    <Swiper
                                      modules={[Navigation, Pagination]}
                                      spaceBetween={0}
                                      slidesPerView={1}
                                      pagination={{ clickable: true }}
                                      navigation
                                      style={{ width: "100%" }}
                                    >
                                      {final.map((url, idx) => (
                                        <SwiperSlide
                                          key={`${property.id}-img-${idx}`}
                                        >
                                          <div className="ratio ratio-4x3">
                                            <img
                                              src={url}
                                              alt={property.name}
                                              className="w-100 h-100 object-fit-cover"
                                              loading="lazy"
                                              style={{
                                                borderRadius: "12px 12px 0 0",
                                              }}
                                            />
                                          </div>
                                        </SwiperSlide>
                                      ))}
                                    </Swiper>

                                    {/* Heart symbol for shortlisted properties - positioned top-right */}
                                    {hasShortlistedRooms(property) && (
                                      <div className="shortlist-heart-container">
                                        <Icon.HeartFill className="shortlist-heart-icon" />
                                      </div>
                                    )}

                                    {/* Badge with "View Details" label - positioned top-right */}
                                    <div className="position-absolute top-0 end-0 m-3">
                                      <span
                                        className="badge bg-primary px-3 py-2"
                                        style={{
                                          fontSize: "0.75rem",
                                          borderRadius: "20px",
                                          fontWeight: "500",
                                        }}
                                      >
                                        View Details
                                      </span>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>

                          <div className="card-body p-3 d-flex flex-column flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="flex-grow-1">
                                {/* Name */}
                                <h6
                                  className="fw-bold text-body mb-2"
                                  style={{ fontSize: "0.95rem" }}
                                >
                                  {property.propertyName || property.name}
                                </h6>

                                <div className="text-muted small">
                                  {/* Location with Zone */}
                                  {property.location && (
                                    <div className="mb-1 d-flex align-items-center">
                                      <div
                                        className="icon-container"
                                        style={{
                                          width: "24px",
                                          minWidth: "24px",
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                        }}
                                      >
                                        <i
                                          className="pi pi-map-marker"
                                          style={{ fontSize: "0.75rem" }}
                                        ></i>
                                      </div>
                                      <span className="text-nowrap">
                                        {property.location.name}
                                        {property.location.zone?.name &&
                                          ` (${property.location.zone.name} )`}
                                      </span>
                                    </div>
                                  )}

                                  {/* Property Type */}
                                  {property.propertyType?.name && (
                                    <div className="mb-1 d-flex align-items-center">
                                      <div
                                        className="icon-container"
                                        style={{
                                          width: "24px",
                                          minWidth: "24px",
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                        }}
                                      >
                                        <i
                                          className="pi pi-building"
                                          style={{ fontSize: "0.75rem" }}
                                        ></i>
                                      </div>
                                      <span className="text-nowrap">
                                        {property.propertyType.name}
                                      </span>
                                    </div>
                                  )}

                                  {/* Room Types Available */}
                                  {property.roomTypes &&
                                    property.roomTypes.length > 0 && (
                                      <div className="mb-1 d-flex align-items-center">
                                        <div
                                          className="icon-container"
                                          style={{
                                            width: "24px",
                                            minWidth: "24px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                          }}
                                        >
                                          <i
                                            className="pi pi-home"
                                            style={{ fontSize: "0.75rem" }}
                                          ></i>
                                        </div>
                                        <span className="text-nowrap">
                                          {property.roomTypes
                                            .map((rt: any) => rt.name)
                                            .filter((name: string) =>
                                              [
                                                "Studio",
                                                "1 Bedroom",
                                                "2 Bedroom",
                                              ].includes(name)
                                            )
                                            .join(", ")}
                                        </span>
                                      </div>
                                    )}

                                  {/* Show category names available for this property (from rooms/categories) */}
                                  {/* {(() => {
                                  const catNames = getPropertyCategoryNames(property);
                                  if (catNames && catNames.length > 0) {
                                    return (
                                      <div className="mb-1 d-flex align-items-start">
                                        <i className="pi pi-tags me-2" style={{ fontSize: '0.75rem', marginTop: '4px' }}></i>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                          {catNames.map((cn, idx) => {
                                            const parts = String(cn).split(',').map(s => s.trim()).filter(Boolean);
                                            if (parts.length > 1) {
                                              // Render comma-separated parts on their own line (stacked)
                                              return (
                                                <div key={idx} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                  {parts.map((p, j) => (
                                                    <span key={`${idx}-${j}`} style={{ fontSize: '0.78rem', color: '#222' }}>{p}</span>
                                                  ))}
                                                </div>
                                              );
                                            }
                                            // Single category
                                            return (
                                              <span key={idx} style={{ fontSize: '0.78rem', color: '#222' }}>{parts[0] || ''}</span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()} */}
                                </div>
                              </div>
                            </div>

                            {/* View Details Button */}
                            <div className="mt-auto">
                              <Link
                                to={`/property/${slugify(property.name)}-${
                                  property.id
                                }`}
                                className="btn btn-primary w-100 py-2"
                                style={{
                                  borderRadius: "8px",
                                  fontSize: "0.9rem",
                                  fontWeight: "500",
                                }}
                                onClick={() => {
                                  const checkInValue =
                                    userRole === "B2BUser"
                                      ? userCheckIn
                                      : dateRange?.[0]?.toISOString() || "";
                                  const checkOutValue =
                                    userRole === "B2BUser"
                                      ? userCheckOut
                                      : dateRange?.[1]?.toISOString() || "";

                                  console.log("Navigation state values:", {
                                    userRole,
                                    checkInValue,
                                    checkOutValue,
                                    userNoOfAdults,
                                    userNoOfChildrens,
                                  });
                                }}
                                state={{
                                  propertyId: property.id,
                                  checkIn:
                                    userRole === "B2BUser"
                                      ? userCheckIn
                                      : dateRange?.[0]?.toISOString() || "",
                                  checkOut:
                                    userRole === "B2BUser"
                                      ? userCheckOut
                                      : dateRange?.[1]?.toISOString() || "",
                                  roomTypeId: Number(
                                    selectedRoomType?.id || roomTypeId
                                  ),
                                  noOfAdults:
                                    userRole === "B2BUser"
                                      ? userNoOfAdults
                                      : undefined,
                                  noOfChildrens:
                                    userRole === "B2BUser"
                                      ? userNoOfChildrens
                                      : undefined,
                                }}
                              >
                                View Details
                              </Link>
                            </div>
                          </div>

                          {/* Distance Display for B2B Users */}
                          {userDetails?.address &&
                            userDetails.address.length > 0 &&
                            property.address && (
                              <div className="border-top p-2">
                                <DistanceDisplay
                                  propertyAddress={property.address}
                                  calculateDistance={
                                    calculateDistanceToProperty
                                  }
                                />
                              </div>
                            )}
                        </article>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-12 col-lg-4">
                {/* Map Container */}
                <div
                  className="position-sticky bg-white shadow-sm border"
                  style={{
                    top: "120px",
                    borderRadius: "12px",
                    height: "calc(100vh - 200px)",
                    minHeight: "400px",
                  }}
                >
                  {isLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "12px",
                      }}
                      center={coordinates[0] || defaultCenter}
                      zoom={13}
                      options={{
                        zoomControl: true,
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                      }}
                    >
                      {coordinates.map((coord, index) => (
                        <Marker
                          key={index}
                          position={coord}
                          onClick={() => setSelectedMarkerIndex(index)}
                        />
                      ))}

                      {selectedMarkerIndex !== null &&
                        coordinates[selectedMarkerIndex] && (
                          <InfoWindow
                            position={coordinates[selectedMarkerIndex]}
                            onCloseClick={() => setSelectedMarkerIndex(null)}
                          >
                            <div className="p-2" style={{ minWidth: "200px" }}>
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  src={
                                    getPropertyImageUrls(
                                      properties[selectedMarkerIndex]
                                    )[0] || "/Images/no-image-placeholder.png"
                                  }
                                  alt="Property"
                                  className="rounded"
                                  style={{
                                    width: "60px",
                                    height: "60px",
                                    objectFit: "cover",
                                  }}
                                />
                                <div>
                                  <h6 className="mb-1 fw-bold">
                                    {properties[selectedMarkerIndex]?.name}
                                  </h6>
                                  <small className="text-muted">
                                    {properties[selectedMarkerIndex]?.address}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </InfoWindow>
                        )}
                    </GoogleMap>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <LoadingSpinner size="md" text="Loading map..." />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="row d-none">
              <div className="col-md-12">
                <div className="hero-searchbar bg-light d-none d-lg-flex flex-row gap-3">
                  {userRole !== "B2BUser" && (
                    <div className="d-flex flex-row gap-2">
                      <label htmlFor="location" className="">
                        Search by Location
                      </label>
                      <div className="avatar avatar-md rounded-circle ">
                        <span className="icon-box border tx-22">
                          {" "}
                          <Icon.GeoAlt className="text-muted" />{" "}
                        </span>
                      </div>
                      <Dropdown
                        value={selectedCity ? selectedCity.id : null}
                        options={cities}
                        onChange={(e) => {
                          const city = cities.find(
                            (c: any) => c.id === e.value
                          );
                          setSelectedCity(city || null);
                        }}
                        placeholder="Search City"
                        id="location"
                        name="location"
                        appendTo="self"
                        optionLabel="name"
                        optionValue="id"
                        className="w-100"
                      />
                    </div>
                  )}
                  <div className="d-flex flex-row gap-2">
                    <label htmlFor="checkin">Checkin-Checkout Date</label>
                    <div className="avatar avatar-md rounded-circle ">
                      <span className="icon-box border tx-22">
                        {" "}
                        <Icon.CalendarRange className="text-muted" />{" "}
                      </span>
                    </div>
                    <Calendar
                      value={dateRange}
                      onChange={(e) =>
                        setDateRange(e.value as [Date | null, Date | null])
                      }
                      selectionMode="range"
                      readOnlyInput
                      placeholder="Check-In & Check-Out"
                      dateFormat="yy-mm-dd"
                      showIcon
                      hideOnRangeSelection
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="d-flex flex-row gap-2">
                    <label htmlFor="location">Search by Room Type</label>
                    <div className="avatar avatar-md rounded-circle ">
                      <span className="icon-box border tx-22">
                        {" "}
                        <Icon.Buildings className="text-muted" />{" "}
                      </span>
                    </div>
                    <Dropdown
                      value={selectedRoomType ? selectedRoomType.id : null}
                      options={roomTypes}
                      onChange={(e) => {
                        const roomType = roomTypes.find(
                          (rt: any) => rt.id === e.value
                        );
                        setSelectedRoomType(roomType || null);
                      }}
                      placeholder="Room type"
                      id="roomType"
                      name="roomType"
                      appendTo="self"
                      optionLabel="name"
                      optionValue="id"
                      className="w-100"
                    />
                  </div>
                  <div className="search-btn align-self-end">
                    <Button
                      onClick={handleSearch}
                      label="Filter"
                      severity="secondary"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row  d-none">
              <div className="col-md-12 mb-3">
                <form className="row g-2 align-items-center">
                  {userRole !== "B2BUser" && (
                    <div className="col-md-3">
                      <AutocompleteInput<ListItem>
                        items={cities}
                        value={selectedCity}
                        onChange={handleCityChange}
                        placeholder="Search City"
                        showAllOnFocus={true}
                      />
                    </div>
                  )}
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="Check-In"
                      value={localCheckIn}
                      onChange={(e) => setLocalCheckIn(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="Check-Out"
                      value={localCheckOut}
                      onChange={(e) => setLocalCheckOut(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <AutocompleteInput<ListItem>
                      items={roomTypes.map((rt) => ({
                        id: rt.id,
                        name: rt.name,
                      }))}
                      value={
                        roomTypes.find((rt) => rt.name === roomTypeInput)
                          ? {
                              id: roomTypes.find(
                                (rt) => rt.name === roomTypeInput
                              )!.id,
                              name: roomTypeInput,
                            }
                          : selectedRoomType
                      }
                      onChange={(item) => {
                        setRoomTypeInput(item ? item.name : "");
                        setSelectedRoomType(item);
                      }}
                      placeholder="Room type"
                      showAllOnFocus={true}
                    />
                  </div>
                  <div className="col-md-2 text-end">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleSearch}
                    >
                      Search
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="row d-none">
              <div className="col-md-6">
                <div className="row">
                  {loading ? (
                    <div className="text-center py-5">Loading...</div>
                  ) : properties.length === 0 ? (
                    <div className="text-center py-5">No properties found.</div>
                  ) : (
                    properties.map((property) => (
                      <div className="col-md-6" key={property.id}>
                        <div className="property-card">
                          {/* Instead of Link, use navigate with state/props */}
                          <div
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              navigate(
                                `/property/${slugify(property.name)}-${
                                  property.id
                                }`,
                                { state: { propertyId: property.id } }
                              )
                            }
                          >
                            <img
                              src={"/Images/property1.jpg"}
                              alt={property.name}
                              className="img-fluid rounded"
                              style={{
                                aspectRatio: "1/1",
                                objectFit: "cover",
                                width: "100%",
                              }}
                            />
                            <h5>{property.propertyName || property.name}</h5>
                            <p>{property.address}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="col-md-6"></div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default ListProperties;
